import { notification } from "antd";
import AuthService from "../../base/services/authentication.service";
import { LocalStorageService } from "../../base/services/local-storage.service";
import { isPublicSurveyRoute } from "../../utils/constants";
import { describeBillingReason, describeOrganizationLock } from "./billingCodes";
import { getStoredBillingStatus, isBillingOnly, isHeldByGate, useBillingStore } from "./billingStore";

/**
 * The 402 gate, as the response interceptor (base/utils/http.js) hands it over.
 *
 * With the new billing flow on, an organization held at billing_only has every
 * call outside the billing, account and credits endpoints answered with a 402,
 * whose `code` is the reason it is held. It can happen at any moment — a trial
 * ends, a renewal is declined — so this is where the app finds out: the
 * organization is marked billing_only in the billing store, and the routes,
 * which watch the store, put the billing page in front of whatever was open —
 * or, for somebody who can't manage its billing, take them out of it.
 *
 * Several requests usually fail together, so the notice is raised once, on the
 * change, and under one key.
 */
export function handlePaymentRequired(body) {
   // A respondent filling in a public survey is not the organization's to
   // send anywhere, and nobody signed in has nothing to be held from.
   if (isPublicSurveyRoute() || !AuthService.isAuthenticated()) return;

   const organizationId = AuthService.getOrganizationId();
   if (!organizationId) return;

   const reason = typeof body?.code === "string" ? body.code : null;
   const wasHeld =
      isBillingOnly(getStoredBillingStatus(organizationId)) || isHeldByGate(organizationId);

   useBillingStore.getState().markBillingRequired(organizationId, reason);

   if (!wasHeld) announceBillingRequired(reason);
}

/**
 * The notice an organization just held at billing_only is shown, for `reason`,
 * whichever way the app heard of it: a 402 here, or a refreshed session's
 * billing status (billingSignIn.js). One key, so it is never shown twice.
 *
 * Somebody who can't manage its billing is not sent to the billing page but
 * out of the organization (Routes.js), and is told why instead.
 */
export function announceBillingRequired(reason) {
   if (!useBillingStore.getState().canManageBilling) {
      announceOrganizationLock(reason, LocalStorageService.getItem("organizationName"));
      return;
   }

   notification.warning({
      key: "billing-required",
      message: "Heads up!",
      description: describeBillingReason(
         reason,
         getStoredBillingStatus(AuthService.getOrganizationId())
      ).title
   });
}

/**
 * The notice a member is shown as they are kept out of an organization held
 * at billing_only, for `reason`: on the way out of it, or on trying to open it.
 */
export function announceOrganizationLock(reason, organizationName) {
   const lock = describeOrganizationLock(reason, organizationName);
   notification.warning({
      key: "billing-required",
      message: "Heads up!",
      description: `${lock.title}. ${lock.description}`
   });
}
